import { lazy, Suspense, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
/*
   Importação direta do arquivo, não do barril: o cabeçalho está em toda página
   e importar de `@/components/ui` ou `@/features/cart` traria a biblioteca
   inteira e todo o Radix para o pacote inicial.
*/
import { Button } from '@/components/ui/Button';
import { CartButton } from '@/features/cart/CartButton';
import { env } from '@/lib/env';

/*
   A gaveta do carrinho é carregada só quando alguém a abre.

   Ela traz o painel do Radix, a lista de itens, o seletor de unidade e o motor
   de preço — e a maioria das visitas nunca clica no ícone. Cobrar esse peso de
   todo mundo no primeiro carregamento seria pagar pelo caso menos frequente.
*/
const CartSheet = lazy(() =>
  import('@/features/cart/CartSheet').then((m) => ({ default: m.CartSheet })),
);

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/sobre', label: 'Sobre Nós' },
  { to: '/exames', label: 'Exames' },
  { to: '/unidades', label: 'Unidades' },
  { to: '/contato', label: 'Contato' },
];

const PORTAL_RESULTADOS =
  'https://santaizabel.shiftcloud.com.br/shift/lis/santaizabel/elis/s01.iu.web.Login.cls';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [carrinhoAberto, setCarrinhoAberto] = useState(false);

  const whatsapp = `https://api.whatsapp.com/send?phone=${env.VITE_WHATSAPP}`;

  return (
    <>
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8 h-20 md:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0" onClick={() => setOpen(false)}>
            <img
              src="/logo.png"
              alt="Santa Izabel Medicina Laboratorial"
              className="h-12 sm:h-14 md:h-[4.5rem] w-auto shrink-0 object-contain"
              width={288}
              height={144}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? 'text-santa-green' : 'text-slate-600 hover:text-santa-green'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/viver-mais"
              className="text-sm font-medium text-santa-gold hover:text-yellow-600 transition-colors"
            >
              Cartão Viver+
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {/*
              Carrinho antes dos botões de ação, e visível em todas as larguras.
              Escondê-lo no celular esconderia o funil justamente onde ele mais
              acontece — o tráfego de busca por preço de exame é majoritariamente
              móvel.
            */}
            <CartButton onClick={() => setCarrinhoAberto(true)} />

            <Button
              variant="ghost"
              className="hidden lg:inline-flex"
              href={PORTAL_RESULTADOS}
              target="_blank"
              rel="noopener noreferrer"
            >
              Resultados
            </Button>

            {/*
              O WhatsApp deixa de ser O funil e passa a ser o apoio do funil.
              Mantido de propósito: é o canal que o laboratório já usa, e tirá-lo
              antes de o checkout provar que converte seria trocar um caminho
              conhecido por um não testado.
            */}
            <Button
              variant="primary"
              className="hidden sm:inline-flex"
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
            >
              Agendar
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={open}
              aria-controls="menu-mobile"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {open && (
          <nav id="menu-mobile" className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className="py-3 px-2 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-100 hover:text-santa-green transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/viver-mais"
                onClick={() => setOpen(false)}
                className="py-3 px-2 rounded-lg text-base font-semibold text-santa-gold hover:bg-amber-50 transition-colors"
              >
                Cartão Viver+
              </Link>

              <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-slate-100">
                <Button
                  variant="outline"
                  className="w-full"
                  href={PORTAL_RESULTADOS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Resultados
                </Button>
                <Button
                  variant="primary"
                  className="w-full"
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Agendar
                </Button>
              </div>
            </div>
          </nav>
        )}
      </header>

      {/*
        Só monta depois do primeiro clique. Sem `fallback` visível: a gaveta
        anima ao entrar, e um indicador de carga piscando antes dela seria mais
        ruído que informação num pedaço de poucos quilobytes.
      */}
      {carrinhoAberto && (
        <Suspense fallback={null}>
          <CartSheet aberto={carrinhoAberto} onFechar={() => setCarrinhoAberto(false)} />
        </Suspense>
      )}
    </>
  );
}
